// src/cockpit4/components/HermesCrossDeptCoordinator.tsx
// ============================================
// Hermes 跨部门协调（Hermes 助手 P1-功能6）
//
// 功能定位：
//   Hermes 作为企业协作中枢，统一调度跨部门任务、追踪进度、
//   在阻塞时升级问题、整理沟通记录，并分析协作效率。
//   1. 协调任务列表（按状态/优先级/部门筛选）
//   2. 创建协调任务（选择发起/接收部门、优先级、截止日期）
//   3. 任务分配和追踪（状态流转：待处理→进行中→完成/阻塞/升级）
//   4. 沟通记录（每个任务可添加沟通消息）
//   5. 问题升级（阻塞任务可升级，标记升级原因）
//   6. 协作效率分析（统计各部门任务完成率/平均耗时/阻塞率）
//   7. 跨模块工作流触发（从任务触发接收部门 Runtime 的操作）
//
// 数据来源（禁止假数据）：
//   - 协调任务及沟通记录：localStorage（key: xeaos_hermes_coordination）持久化
//   - 跨模块状态：通过 RuntimeManager.query 各模块 Runtime 获取真实状态
//       · 营销中心 → MarketingOS / BusinessEngine
//       · 媒体工作室 → MediaStudio
//       · 客户中心 → CRM
//       · 知识中心 → Knowledge
//       · 财务中心 → RevenueEngine
//       · 工作流中心 → Workflow
//       · 总控台 → CEO
//   - 无数据时显示"暂无协调任务 / 点击创建"
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Network,
  Plus,
  X,
  Filter,
  Send,
  AlertTriangle,
  ArrowUpCircle,
  CheckCircle2,
  Clock,
  Layers,
  TrendingUp,
  Zap,
  RefreshCw,
  Trash2,
  Pencil,
  ChevronRight,
  Crown,
  Megaphone,
  Video,
  Users,
  BookOpen,
  Briefcase,
  Wallet,
  GitBranch,
  Settings,
  CircleDot,
  PauseCircle,
  Flame,
  MessageSquare,
  ArrowRight,
  Activity,
  type LucideIcon,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

// ============================================
// 类型定义（对齐规格数据模型）
// ============================================

type Department =
  | "ceo"
  | "marketing"
  | "media"
  | "crm"
  | "knowledge"
  | "project"
  | "finance"
  | "workflow"
  | "settings";

type CoordinationStatus =
  | "pending"
  | "in_progress"
  | "blocked"
  | "completed"
  | "escalated";

type Priority = "low" | "medium" | "high" | "urgent";

interface Communication {
  id: string;
  /** 发送人/部门 */
  from: string;
  /** 沟通内容 */
  content: string;
  timestamp: string;
}

interface CoordinationTask {
  id: string;
  title: string;
  description: string;
  priority: Priority;
  status: CoordinationStatus;
  /** 发起部门 */
  fromDept: Department;
  /** 接收部门 */
  toDept: Department;
  /** 负责人 */
  assignee?: string;
  /** 截止日期 YYYY-MM-DD */
  dueDate?: string;
  /** 依赖的其他协调任务 ID */
  dependsOn?: string[];
  /** 沟通记录 */
  communications: Communication[];
  /** 是否升级 */
  escalated?: boolean;
  escalatedReason?: string;
  escalatedAt?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

/** localStorage 持久化结构 */
interface CoordinationStore {
  tasks: CoordinationTask[];
}

// ============================================
// 常量配置
// ============================================

const STORAGE_KEY = "xeaos_hermes_coordination";

/** 部门元信息映射（对齐规格） */
const DEPT_META: Record<Department, { label: string; color: string; icon: LucideIcon }> = {
  ceo: { label: "总控台", color: "#A855F7", icon: Crown },
  marketing: { label: "营销中心", color: "#EC4899", icon: Megaphone },
  media: { label: "媒体工作室", color: "#06B6D4", icon: Video },
  crm: { label: "客户中心", color: "#10B981", icon: Users },
  knowledge: { label: "知识中心", color: "#3B82F6", icon: BookOpen },
  project: { label: "项目中心", color: "#F59E0B", icon: Briefcase },
  finance: { label: "财务中心", color: "#EF4444", icon: Wallet },
  workflow: { label: "工作流中心", color: "#8B5CF6", icon: GitBranch },
  settings: { label: "设置中心", color: "#6B7280", icon: Settings },
};

/** 部门 → RuntimeName 映射（用于跨模块状态查询与工作流触发） */
const DEPT_RUNTIME: Partial<Record<Department, RuntimeName>> = {
  ceo: RuntimeName.CEO,
  marketing: RuntimeName.MarketingOS,
  media: RuntimeName.MediaStudio,
  crm: RuntimeName.CRM,
  knowledge: RuntimeName.Knowledge,
  finance: RuntimeName.RevenueEngine,
  workflow: RuntimeName.Workflow,
};

/** 协调状态元信息 */
const STATUS_META: Record<
  CoordinationStatus,
  { label: string; color: string; icon: LucideIcon }
> = {
  pending: { label: "待处理", color: "#6B7280", icon: CircleDot },
  in_progress: { label: "进行中", color: "#3B82F6", icon: Activity },
  blocked: { label: "阻塞", color: "#F59E0B", icon: PauseCircle },
  completed: { label: "已完成", color: "#10B981", icon: CheckCircle2 },
  escalated: { label: "已升级", color: "#EF4444", icon: Flame },
};

/** 优先级元信息 */
const PRIORITY_META: Record<Priority, { label: string; color: string }> = {
  low: { label: "低", color: "#6B7280" },
  medium: { label: "中", color: "#3B82F6" },
  high: { label: "高", color: "#F59E0B" },
  urgent: { label: "紧急", color: "#EF4444" },
};

/** 看板分列顺序 */
const KANBAN_COLUMNS: CoordinationStatus[] = [
  "pending",
  "in_progress",
  "blocked",
  "completed",
];

/** 紫色主色调 */
const PRIMARY = "#A855F7";

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(prefix = "c"): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 读取 localStorage 全量状态 */
function loadStore(): CoordinationStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { tasks: [] };
    const parsed = JSON.parse(raw) as CoordinationStore;
    if (!parsed || !Array.isArray(parsed.tasks)) return { tasks: [] };
    return parsed;
  } catch {
    return { tasks: [] };
  }
}

/** 写入 localStorage 全量状态 */
function saveStore(store: CoordinationStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // 静默降级
  }
}

/** 格式化时间为相对时间描述 */
function formatRelativeTime(iso: string): string {
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diff = Math.max(0, now - t);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "刚刚";
  if (min < 60) return `${min} 分钟前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} 天前`;
  return new Date(iso).toLocaleDateString("zh-CN");
}

/** 计算任务耗时（小时），从创建到完成 */
function calcDurationHours(task: CoordinationTask): number | null {
  if (!task.completedAt) return null;
  const start = new Date(task.createdAt).getTime();
  const end = new Date(task.completedAt).getTime();
  return Math.max(0, (end - start) / 3600000);
}

/** 获取今日日期 YYYY-MM-DD */
function todayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

// ============================================
// 跨模块状态查询（基于 RuntimeManager.query 真实数据）
// ============================================

interface DeptRuntimeStatus {
  /** Runtime 是否已注册 */
  registered: boolean;
  /** Runtime 状态 */
  status: string;
  /** 健康 */
  health: string;
  /** 简要数据摘要 */
  summary?: string;
  /** 最近一次查询错误 */
  error?: string;
}

/** 查询单个部门的 Runtime 真实状态 */
async function queryDeptStatus(dept: Department): Promise<DeptRuntimeStatus> {
  const runtimeName = DEPT_RUNTIME[dept];
  if (!runtimeName) {
    return { registered: false, status: "n/a", health: "n/a" };
  }
  if (!RuntimeManager.hasRuntime(runtimeName)) {
    return { registered: false, status: "未注册", health: "n/a" };
  }
  try {
    const resp = await RuntimeManager.query(runtimeName, { type: "status" }).catch(
      () => null
    );
    const statusSummary = RuntimeManager.getStatusSummary();
    const rtStatus = statusSummary.status[runtimeName] ?? "unknown";
    const rtHealth = statusSummary.health[runtimeName] ?? "unknown";
    let summary: string | undefined;
    if (resp?.success && resp.data) {
      const data = resp.data as Record<string, unknown>;
      // 兼容多种字段命名
      summary =
        (data.summary as string) ??
        (data.message as string) ??
        (typeof data.totalTasks === "number" ? `任务数 ${data.totalTasks}` : undefined) ??
        (typeof data.total === "number" ? `总数 ${data.total}` : undefined);
    }
    return {
      registered: true,
      status: rtStatus,
      health: rtHealth,
      summary,
      error: resp && !resp.success ? resp.error : undefined,
    };
  } catch (err) {
    return {
      registered: true,
      status: "error",
      health: "unknown",
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/** 触发跨模块工作流：在接收部门 Runtime 上执行操作 */
async function triggerCrossModuleAction(
  task: CoordinationTask
): Promise<{ success: boolean; message: string }> {
  const runtimeName = DEPT_RUNTIME[task.toDept];
  if (!runtimeName) {
    return {
      success: false,
      message: `${DEPT_META[task.toDept].label} 未绑定 Runtime，无法触发`,
    };
  }
  if (!RuntimeManager.hasRuntime(runtimeName)) {
    return {
      success: false,
      message: `${DEPT_META[task.toDept].label} 的 Runtime（${runtimeName}）未注册`,
    };
  }
  try {
    const resp = await RuntimeManager.execute(runtimeName, "coordination_trigger", {
      taskId: task.id,
      title: task.title,
      description: task.description,
      fromDept: task.fromDept,
      toDept: task.toDept,
      priority: task.priority,
    }).catch((e) => ({ success: false, error: e instanceof Error ? e.message : String(e) }));
    if (resp?.success) {
      return {
        success: true,
        message: `已向 ${DEPT_META[task.toDept].label}（${runtimeName}）触发工作流`,
      };
    }
    return {
      success: false,
      message: `触发失败：${resp?.error ?? "未知错误"}`,
    };
  } catch (err) {
    return {
      success: false,
      message: `触发异常：${err instanceof Error ? err.message : String(err)}`,
    };
  }
}

// ============================================
// 主组件
// ============================================

export default function HermesCrossDeptCoordinator() {
  const [store, setStore] = useState<CoordinationStore>(() => loadStore());
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterStatus, setFilterStatus] = useState<CoordinationStatus | "all">("all");
  const [filterPriority, setFilterPriority] = useState<Priority | "all">("all");
  const [filterDept, setFilterDept] = useState<Department | "all">("all");
  const [deptStatuses, setDeptStatuses] = useState<Record<Department, DeptRuntimeStatus>>(
    () => {
      const initial = {} as Record<Department, DeptRuntimeStatus>;
      (Object.keys(DEPT_META) as Department[]).forEach((d) => {
        initial[d] = { registered: false, status: "未查询", health: "n/a" };
      });
      return initial;
    }
  );
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [triggeringTaskId, setTriggeringTaskId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // ============================================
  // 持久化：每次 store 变更都写回 localStorage
  // ============================================
  useEffect(() => {
    saveStore(store);
  }, [store]);

  // ============================================
  // Toast 自动消失
  // ============================================
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // ============================================
  // 刷新跨模块状态
  // ============================================
  const refreshDeptStatuses = useCallback(async () => {
    setRefreshingStatus(true);
    try {
      const depts = Object.keys(DEPT_META) as Department[];
      const entries = await Promise.all(
        depts.map(async (d) => [d, await queryDeptStatus(d)] as const)
      );
      const map = {} as Record<Department, DeptRuntimeStatus>;
      entries.forEach(([d, s]) => {
        map[d] = s;
      });
      setDeptStatuses(map);
      setToast("跨模块状态已刷新");
    } catch (err) {
      setToast(`状态刷新失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setRefreshingStatus(false);
    }
  }, []);

  // 首次挂载查询一次
  useEffect(() => {
    refreshDeptStatuses();
    // 仅在挂载时执行一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // 协作效率统计
  // ============================================
  const stats = useMemo(() => {
    const tasks = store.tasks;
    const total = tasks.length;
    const byStatus = (s: CoordinationStatus) =>
      tasks.filter((t) => t.status === s).length;
    const inProgress = byStatus("in_progress");
    const blocked = byStatus("blocked");
    const escalated = tasks.filter((t) => t.escalated).length;
    const completed = byStatus("completed");
    const completionRate = total > 0 ? (completed / total) * 100 : 0;
    // 完成任务平均耗时
    const completedTasks = tasks.filter((t) => t.completedAt);
    const avgDuration =
      completedTasks.length > 0
        ? completedTasks.reduce((sum, t) => sum + (calcDurationHours(t) ?? 0), 0) /
          completedTasks.length
        : 0;
    // 阻塞率：曾经阻塞或当前阻塞的任务占比
    const blockedRate = total > 0 ? ((blocked + escalated) / total) * 100 : 0;
    return {
      total,
      inProgress,
      blocked,
      escalated,
      completed,
      completionRate,
      avgDuration,
      blockedRate,
    };
  }, [store.tasks]);

  // ============================================
  // 部门协作统计（用于热力图和效率分析）
  // ============================================
  const deptStats = useMemo(() => {
    const depts = Object.keys(DEPT_META) as Department[];
    const map = {} as Record<
      Department,
      {
        total: number;
        completed: number;
        blocked: number;
        escalated: number;
        completionRate: number;
        avgDuration: number;
      }
    >;
    depts.forEach((d) => {
      // 统计该部门作为发起或接收的任务
      const involved = store.tasks.filter((t) => t.fromDept === d || t.toDept === d);
      const completed = involved.filter((t) => t.status === "completed");
      const completedWithDuration = completed.filter((t) => t.completedAt);
      const avgDuration =
        completedWithDuration.length > 0
          ? completedWithDuration.reduce(
              (sum, t) => sum + (calcDurationHours(t) ?? 0),
              0
            ) / completedWithDuration.length
          : 0;
      map[d] = {
        total: involved.length,
        completed: completed.length,
        blocked: involved.filter((t) => t.status === "blocked").length,
        escalated: involved.filter((t) => t.escalated).length,
        completionRate:
          involved.length > 0 ? (completed.length / involved.length) * 100 : 0,
        avgDuration,
      };
    });
    return map;
  }, [store.tasks]);

  /** 部门协作矩阵：fromDept → toDept 的任务数 */
  const collaborationMatrix = useMemo(() => {
    const depts = Object.keys(DEPT_META) as Department[];
    const matrix: Record<string, number> = {};
    depts.forEach((from) => {
      depts.forEach((to) => {
        const count = store.tasks.filter(
          (t) => t.fromDept === from && t.toDept === to
        ).length;
        matrix[`${from}->${to}`] = count;
      });
    });
    // 最大值用于热力图强度计算
    const max = Math.max(1, ...Object.values(matrix));
    return { matrix, max };
  }, [store.tasks]);

  // ============================================
  // 筛选 + 看板分组
  // ============================================
  const filteredTasks = useMemo(() => {
    return store.tasks.filter((t) => {
      if (filterStatus !== "all" && t.status !== filterStatus) return false;
      if (filterPriority !== "all" && t.priority !== filterPriority) return false;
      if (
        filterDept !== "all" &&
        t.fromDept !== filterDept &&
        t.toDept !== filterDept
      )
        return false;
      return true;
    });
  }, [store.tasks, filterStatus, filterPriority, filterDept]);

  const kanbanGroups = useMemo(() => {
    const groups: Record<CoordinationStatus, CoordinationTask[]> = {
      pending: [],
      in_progress: [],
      blocked: [],
      completed: [],
      escalated: [],
    };
    filteredTasks.forEach((t) => {
      groups[t.status].push(t);
    });
    // 每列按优先级降序、再按创建时间倒序
    const priorityWeight: Record<Priority, number> = {
      urgent: 0,
      high: 1,
      medium: 2,
      low: 3,
    };
    Object.keys(groups).forEach((k) => {
      groups[k as CoordinationStatus].sort((a, b) => {
        const w = priorityWeight[a.priority] - priorityWeight[b.priority];
        if (w !== 0) return w;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
    });
    return groups;
  }, [filteredTasks]);

  // ============================================
  // 选中任务
  // ============================================
  const selectedTask = useMemo(
    () => store.tasks.find((t) => t.id === selectedTaskId) ?? null,
    [store.tasks, selectedTaskId]
  );

  // ============================================
  // 操作：创建协调任务
  // ============================================
  const createTask = useCallback(
    (data: {
      title: string;
      description: string;
      priority: Priority;
      fromDept: Department;
      toDept: Department;
      assignee?: string;
      dueDate?: string;
    }) => {
      const now = new Date().toISOString();
      const task: CoordinationTask = {
        id: genId(),
        title: data.title,
        description: data.description,
        priority: data.priority,
        status: "pending",
        fromDept: data.fromDept,
        toDept: data.toDept,
        assignee: data.assignee,
        dueDate: data.dueDate,
        communications: [],
        createdAt: now,
        updatedAt: now,
      };
      setStore((prev) => ({ tasks: [task, ...prev.tasks] }));
      setSelectedTaskId(task.id);
      setShowCreate(false);
      setToast("协调任务已创建");
    },
    []
  );

  // ============================================
  // 操作：状态流转
  // ============================================
  const transitionStatus = useCallback(
    (taskId: string, next: CoordinationStatus) => {
      setStore((prev) => ({
        tasks: prev.tasks.map((t) => {
          if (t.id !== taskId) return t;
          const now = new Date().toISOString();
          return {
            ...t,
            status: next,
            completedAt: next === "completed" ? now : t.completedAt,
            updatedAt: now,
          };
        }),
      }));
      setToast(`状态已更新为「${STATUS_META[next].label}」`);
    },
    []
  );

  // ============================================
  // 操作：升级任务（阻塞 → 升级）
  // ============================================
  const escalateTask = useCallback(
    (taskId: string, reason: string) => {
      setStore((prev) => ({
        tasks: prev.tasks.map((t) => {
          if (t.id !== taskId) return t;
          const now = new Date().toISOString();
          return {
            ...t,
            status: "escalated",
            escalated: true,
            escalatedReason: reason,
            escalatedAt: now,
            updatedAt: now,
          };
        }),
      }));
      setToast("任务已升级，等待更高层级介入");
    },
    []
  );

  // ============================================
  // 操作：添加沟通记录
  // ============================================
  const addCommunication = useCallback(
    (taskId: string, from: string, content: string) => {
      if (!content.trim()) return;
      const comm: Communication = {
        id: genId("m"),
        from: from.trim() || "匿名",
        content: content.trim(),
        timestamp: new Date().toISOString(),
      };
      setStore((prev) => ({
        tasks: prev.tasks.map((t) =>
          t.id === taskId
            ? {
                ...t,
                communications: [...t.communications, comm],
                updatedAt: new Date().toISOString(),
              }
            : t
        ),
      }));
    },
    []
  );

  // ============================================
  // 操作：删除任务
  // ============================================
  const deleteTask = useCallback(
    (taskId: string) => {
      setStore((prev) => ({ tasks: prev.tasks.filter((t) => t.id !== taskId) }));
      if (selectedTaskId === taskId) setSelectedTaskId(null);
      setToast("任务已删除");
    },
    [selectedTaskId]
  );

  // ============================================
  // 操作：触发跨模块工作流
  // ============================================
  const handleTrigger = useCallback(
    async (task: CoordinationTask) => {
      setTriggeringTaskId(task.id);
      try {
        const result = await triggerCrossModuleAction(task);
        // 自动追加一条沟通记录
        const comm: Communication = {
          id: genId("m"),
          from: "Hermes 协调中枢",
          content: result.message,
          timestamp: new Date().toISOString(),
        };
        setStore((prev) => ({
          tasks: prev.tasks.map((t) =>
            t.id === task.id
              ? {
                  ...t,
                  communications: [...t.communications, comm],
                  // 若任务仍为待处理，自动流转为进行中
                  status: t.status === "pending" ? "in_progress" : t.status,
                  updatedAt: new Date().toISOString(),
                }
              : t
          ),
        }));
        setToast(result.message);
      } finally {
        setTriggeringTaskId(null);
      }
    },
    []
  );

  // ============================================
  // 渲染辅助：部门徽标
  // ============================================
  const renderDeptBadge = (dept: Department, size: "sm" | "md" = "sm") => {
    const meta = DEPT_META[dept];
    const Icon = meta.icon;
    const px = size === "sm" ? "px-1.5 py-0.5" : "px-2 py-1";
    const iconSize = size === "sm" ? 10 : 12;
    return (
      <span
        className={`inline-flex items-center gap-1 ${px} rounded text-[9px] font-mono uppercase tracking-wider`}
        style={{ background: `${meta.color}15`, color: meta.color }}
      >
        <Icon size={iconSize} />
        {meta.label}
      </span>
    );
  };

  // ============================================
  // 渲染辅助：优先级标签
  // ============================================
  const renderPriorityTag = (priority: Priority) => {
    const meta = PRIORITY_META[priority];
    return (
      <span
        className="px-1 py-0.5 rounded text-[8px] font-mono uppercase"
        style={{ background: `${meta.color}15`, color: meta.color }}
      >
        {meta.label}
      </span>
    );
  };

  // ============================================
  // 渲染：顶部统计卡片
  // ============================================
  const renderStatsBar = () => {
    const cards: { label: string; value: string | number; icon: LucideIcon; color: string }[] = [
      { label: "总任务", value: stats.total, icon: Layers, color: PRIMARY },
      { label: "进行中", value: stats.inProgress, icon: Activity, color: "#3B82F6" },
      { label: "阻塞", value: stats.blocked, icon: PauseCircle, color: "#F59E0B" },
      { label: "已升级", value: stats.escalated, icon: Flame, color: "#EF4444" },
      {
        label: "完成率",
        value: `${stats.completionRate.toFixed(0)}%`,
        icon: TrendingUp,
        color: "#10B981",
      },
      {
        label: "平均耗时",
        value: stats.avgDuration > 0 ? `${stats.avgDuration.toFixed(1)}h` : "—",
        icon: Clock,
        color: "#8B5CF6",
      },
      {
        label: "阻塞率",
        value: `${stats.blockedRate.toFixed(0)}%`,
        icon: AlertTriangle,
        color: "#EC4899",
      },
    ];
    return (
      <div className="grid grid-cols-7 gap-2">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.label}
              className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2.5 py-2"
            >
              <div className="flex items-center gap-1 mb-1">
                <Icon size={10} style={{ color: c.color }} />
                <span className="text-[8px] font-mono text-gray-500 uppercase tracking-wider">
                  {c.label}
                </span>
              </div>
              <div className="text-[14px] font-mono font-bold text-white leading-none">
                {c.value}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ============================================
  // 渲染：部门协作热力图
  // ============================================
  const renderHeatmap = () => {
    const depts = Object.keys(DEPT_META) as Department[];
    return (
      <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <Network size={11} style={{ color: PRIMARY }} />
            <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
              部门协作热力图
            </span>
          </div>
          <button
            onClick={refreshDeptStatuses}
            disabled={refreshingStatus}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-mono text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 transition-colors disabled:opacity-50"
            title="刷新跨模块 Runtime 状态"
          >
            <RefreshCw size={9} className={refreshingStatus ? "animate-spin" : ""} />
            刷新状态
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[9px] font-mono border-separate border-spacing-0.5">
            <thead>
              <tr>
                <th className="text-gray-600 text-left px-1 py-0.5 sticky left-0 bg-transparent">
                  发起 \ 接收
                </th>
                {depts.map((to) => {
                  const meta = DEPT_META[to];
                  const Icon = meta.icon;
                  return (
                    <th
                      key={to}
                      className="px-1 py-0.5 text-center"
                      title={meta.label}
                    >
                      <Icon size={10} style={{ color: meta.color }} />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {depts.map((from) => {
                const fromMeta = DEPT_META[from];
                const FromIcon = fromMeta.icon;
                return (
                  <tr key={from}>
                    <td
                      className="px-1 py-0.5 text-left whitespace-nowrap sticky left-0"
                      style={{ color: fromMeta.color }}
                      title={fromMeta.label}
                    >
                      <FromIcon size={10} className="inline mr-1" />
                      <span className="text-[8px]">{fromMeta.label}</span>
                    </td>
                    {depts.map((to) => {
                      const count = collaborationMatrix.matrix[`${from}->${to}`];
                      const intensity =
                        collaborationMatrix.max > 0
                          ? count / collaborationMatrix.max
                          : 0;
                      const isSelf = from === to;
                      // 强度映射到紫色透明度
                      const bg =
                        count === 0
                          ? "rgba(255,255,255,0.02)"
                          : `rgba(168,85,247,${0.1 + intensity * 0.6})`;
                      return (
                        <td
                          key={to}
                          className="text-center rounded text-[9px] font-mono"
                          style={{
                            background: bg,
                            color: count > 0 ? "#fff" : "#4b5563",
                            border: isSelf
                              ? "1px dashed rgba(168,85,247,0.2)"
                              : "1px solid rgba(255,255,255,0.03)",
                            minWidth: 24,
                            padding: "2px 4px",
                          }}
                          title={`${fromMeta.label} → ${DEPT_META[to].label}：${count} 项`}
                        >
                          {count > 0 ? count : "·"}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ============================================
  // 渲染：跨模块状态指示器
  // ============================================
  const renderDeptRuntimeStatus = () => {
    const depts = Object.keys(DEPT_META) as Department[];
    return (
      <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Activity size={11} style={{ color: PRIMARY }} />
          <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
            跨模块 Runtime 状态
          </span>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {depts.map((d) => {
            const meta = DEPT_META[d];
            const Icon = meta.icon;
            const st = deptStatuses[d];
            const runtimeName = DEPT_RUNTIME[d];
            // 状态颜色
            let dotColor = "#6B7280";
            if (!runtimeName) dotColor = "#4B5563";
            else if (!st.registered) dotColor = "#6B7280";
            else if (st.status === "ready" && st.health === "healthy")
              dotColor = "#10B981";
            else if (st.status === "error" || st.health === "unhealthy")
              dotColor = "#EF4444";
            else if (st.status === "loading" || st.health === "degraded")
              dotColor = "#F59E0B";
            return (
              <div
                key={d}
                className="flex items-center gap-1.5 px-1.5 py-1 rounded border border-white/[0.04] bg-white/[0.01]"
                title={
                  runtimeName
                    ? `${runtimeName} · ${st.status}/${st.health}${
                        st.error ? ` · ${st.error}` : ""
                      }`
                    : "未绑定 Runtime"
                }
              >
                <Icon size={10} style={{ color: meta.color }} />
                <span className="text-[9px] font-mono text-gray-400 truncate flex-1">
                  {meta.label}
                </span>
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: dotColor }}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ============================================
  // 渲染：看板列
  // ============================================
  const renderKanbanColumn = (status: CoordinationStatus) => {
    const meta = STATUS_META[status];
    const Icon = meta.icon;
    const tasks = kanbanGroups[status];
    return (
      <div className="flex flex-col min-w-0 rounded-md border border-white/[0.06] bg-white/[0.01]">
        <div className="flex items-center justify-between px-2.5 py-2 border-b border-white/[0.04]">
          <div className="flex items-center gap-1.5">
            <Icon size={11} style={{ color: meta.color }} />
            <span
              className="text-[10px] font-mono uppercase tracking-wider"
              style={{ color: meta.color }}
            >
              {meta.label}
            </span>
            <span className="px-1.5 py-0.5 rounded text-[8px] font-mono bg-white/[0.04] text-gray-400">
              {tasks.length}
            </span>
          </div>
        </div>
        <div className="flex-1 p-1.5 space-y-1.5 min-h-[120px]">
          {tasks.length === 0 ? (
            <div className="py-6 text-center">
              <span className="text-[9px] font-mono text-gray-700">— 无 —</span>
            </div>
          ) : (
            tasks.map((t) => {
              const isSelected = t.id === selectedTaskId;
              const toMeta = DEPT_META[t.toDept];
              const ToIcon = toMeta.icon;
              const fromMeta = DEPT_META[t.fromDept];
              const FromIcon = fromMeta.icon;
              const isOverdue =
                t.dueDate &&
                t.dueDate < todayDate() &&
                t.status !== "completed";
              return (
                <motion.button
                  key={t.id}
                  layout
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => setSelectedTaskId(t.id)}
                  className={`w-full text-left rounded-md border p-2 transition-colors ${
                    isSelected
                      ? "border-purple-500/50 bg-purple-500/[0.08]"
                      : "border-white/[0.06] bg-white/[0.02] hover:border-purple-500/30 hover:bg-purple-500/[0.04]"
                  }`}
                >
                  <div className="flex items-start gap-1.5">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 mb-1 flex-wrap">
                        {renderPriorityTag(t.priority)}
                        {t.escalated && (
                          <span className="px-1 py-0.5 rounded text-[8px] font-mono uppercase bg-red-500/15 text-red-400">
                            升级
                          </span>
                        )}
                        {isOverdue && (
                          <span className="px-1 py-0.5 rounded text-[8px] font-mono uppercase bg-amber-500/15 text-amber-400">
                            逾期
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] font-semibold text-white truncate leading-snug">
                        {t.title}
                      </p>
                      {/* 部门流向 */}
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        <span
                          className="inline-flex items-center gap-0.5 text-[8px] font-mono"
                          style={{ color: fromMeta.color }}
                          title={`发起：${fromMeta.label}`}
                        >
                          <FromIcon size={9} />
                          {fromMeta.label}
                        </span>
                        <ArrowRight size={8} className="text-gray-600" />
                        <span
                          className="inline-flex items-center gap-0.5 text-[8px] font-mono"
                          style={{ color: toMeta.color }}
                          title={`接收：${toMeta.label}`}
                        >
                          <ToIcon size={9} />
                          {toMeta.label}
                        </span>
                      </div>
                      {/* 底部信息 */}
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[8px] font-mono text-gray-600">
                          {formatRelativeTime(t.createdAt)}
                        </span>
                        <div className="flex items-center gap-1">
                          {t.communications.length > 0 && (
                            <span className="flex items-center gap-0.5 text-[8px] font-mono text-gray-500">
                              <MessageSquare size={8} />
                              {t.communications.length}
                            </span>
                          )}
                          {t.dueDate && (
                            <span className="flex items-center gap-0.5 text-[8px] font-mono text-gray-500">
                              <Clock size={8} />
                              {t.dueDate.slice(5)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.button>
              );
            })
          )}
        </div>
      </div>
    );
  };

  // ============================================
  // 渲染：看板
  // ============================================
  const renderKanban = () => {
    if (filteredTasks.length === 0 && filterStatus === "all" && filterPriority === "all" && filterDept === "all") {
      return (
        <div className="py-12 text-center">
          <Network size={32} className="mx-auto text-gray-700 mb-2" />
          <p className="text-[11px] font-mono text-gray-500">暂无协调任务</p>
          <p className="text-[9px] font-mono text-gray-600 mt-1">点击「创建任务」开始跨部门协调</p>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-4 gap-2">
        {KANBAN_COLUMNS.map((status) => renderKanbanColumn(status))}
      </div>
    );
  };

  // ============================================
  // 渲染：筛选栏
  // ============================================
  const renderFilters = () => (
    <AnimatePresence>
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="overflow-hidden border-b border-white/[0.04]"
        >
          <div className="px-4 py-2.5 flex items-center gap-3 flex-wrap">
            {/* 状态筛选 */}
            <div className="flex items-center gap-1">
              <span className="text-[9px] font-mono text-gray-500 uppercase">状态</span>
              <select
                value={filterStatus}
                onChange={(e) =>
                  setFilterStatus(e.target.value as CoordinationStatus | "all")
                }
                className="px-2 py-0.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] font-mono text-white outline-none focus:border-purple-500/40"
              >
                <option value="all">全部</option>
                {(Object.keys(STATUS_META) as CoordinationStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_META[s].label}
                  </option>
                ))}
              </select>
            </div>
            {/* 优先级筛选 */}
            <div className="flex items-center gap-1">
              <span className="text-[9px] font-mono text-gray-500 uppercase">优先级</span>
              <select
                value={filterPriority}
                onChange={(e) =>
                  setFilterPriority(e.target.value as Priority | "all")
                }
                className="px-2 py-0.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] font-mono text-white outline-none focus:border-purple-500/40"
              >
                <option value="all">全部</option>
                {(Object.keys(PRIORITY_META) as Priority[]).map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_META[p].label}
                  </option>
                ))}
              </select>
            </div>
            {/* 部门筛选 */}
            <div className="flex items-center gap-1">
              <span className="text-[9px] font-mono text-gray-500 uppercase">部门</span>
              <select
                value={filterDept}
                onChange={(e) =>
                  setFilterDept(e.target.value as Department | "all")
                }
                className="px-2 py-0.5 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] font-mono text-white outline-none focus:border-purple-500/40"
              >
                <option value="all">全部</option>
                {(Object.keys(DEPT_META) as Department[]).map((d) => (
                  <option key={d} value={d}>
                    {DEPT_META[d].label}
                  </option>
                ))}
              </select>
            </div>
            {/* 重置 */}
            <button
              onClick={() => {
                setFilterStatus("all");
                setFilterPriority("all");
                setFilterDept("all");
              }}
              className="ml-auto px-2 py-0.5 rounded text-[9px] font-mono text-gray-400 bg-white/[0.03] hover:bg-white/[0.05] transition-colors"
            >
              重置筛选
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // ============================================
  // 渲染：选中任务详情
  // ============================================
  const renderTaskDetail = () => {
    if (!selectedTask) {
      return (
        <div className="py-8 text-center">
          <ChevronRight size={20} className="mx-auto text-gray-700 mb-1" />
          <p className="text-[10px] font-mono text-gray-500">
            选择左侧任务查看详情、沟通记录与操作
          </p>
        </div>
      );
    }

    const toMeta = DEPT_META[selectedTask.toDept];
    const statusMeta = STATUS_META[selectedTask.status];
    const StatusIcon = statusMeta.icon;
    const isTriggering = triggeringTaskId === selectedTask.id;
    const toRuntimeName = DEPT_RUNTIME[selectedTask.toDept];
    const canTrigger = !!toRuntimeName;

    return (
      <div className="space-y-3">
        {/* 任务标题与状态 */}
        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <h4 className="text-[13px] font-semibold text-white leading-snug">
                {selectedTask.title}
              </h4>
              <p className="text-[10px] text-gray-400 mt-1 leading-relaxed">
                {selectedTask.description || "（无描述）"}
              </p>
            </div>
            <span
              className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase flex-shrink-0"
              style={{ background: `${statusMeta.color}15`, color: statusMeta.color }}
            >
              <StatusIcon size={10} />
              {statusMeta.label}
            </span>
          </div>

          {/* 元信息 */}
          <div className="grid grid-cols-2 gap-2 mt-2 text-[9px] font-mono">
            <div className="flex items-center gap-1">
              <span className="text-gray-500">优先级：</span>
              <span style={{ color: PRIORITY_META[selectedTask.priority].color }}>
                {PRIORITY_META[selectedTask.priority].label}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">截止：</span>
              <span className="text-gray-300">
                {selectedTask.dueDate ?? "未设定"}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">负责人：</span>
              <span className="text-gray-300">
                {selectedTask.assignee ?? "未分配"}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">创建：</span>
              <span className="text-gray-300">
                {formatRelativeTime(selectedTask.createdAt)}
              </span>
            </div>
          </div>

          {/* 部门流向 */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {renderDeptBadge(selectedTask.fromDept, "md")}
            <ArrowRight size={12} className="text-gray-600" />
            {renderDeptBadge(selectedTask.toDept, "md")}
            {toRuntimeName && (
              <span className="text-[8px] font-mono text-gray-600">
                runtime: {toRuntimeName}
              </span>
            )}
          </div>

          {/* 升级信息 */}
          {selectedTask.escalated && (
            <div className="mt-2 px-2 py-1.5 rounded border border-red-500/30 bg-red-500/[0.06]">
              <div className="flex items-center gap-1 mb-0.5">
                <Flame size={10} className="text-red-400" />
                <span className="text-[9px] font-mono text-red-300 uppercase">
                  已升级
                </span>
                {selectedTask.escalatedAt && (
                  <span className="text-[8px] font-mono text-gray-500 ml-auto">
                    {formatRelativeTime(selectedTask.escalatedAt)}
                  </span>
                )}
              </div>
              <p className="text-[9px] text-gray-300 leading-snug">
                {selectedTask.escalatedReason || "未填写升级原因"}
              </p>
            </div>
          )}
        </div>

        {/* 状态流转操作 */}
        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-2">
            状态流转
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {selectedTask.status !== "in_progress" &&
              selectedTask.status !== "completed" && (
                <button
                  onClick={() => transitionStatus(selectedTask.id, "in_progress")}
                  className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 transition-colors"
                >
                  <Activity size={10} />
                  启动
                </button>
              )}
            {selectedTask.status !== "completed" && (
              <button
                onClick={() => transitionStatus(selectedTask.id, "completed")}
                className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors"
              >
                <CheckCircle2 size={10} />
                完成
              </button>
            )}
            {selectedTask.status !== "blocked" &&
              selectedTask.status !== "completed" && (
                <button
                  onClick={() => transitionStatus(selectedTask.id, "blocked")}
                  className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 transition-colors"
                >
                  <PauseCircle size={10} />
                  标记阻塞
                </button>
              )}
            {(selectedTask.status === "blocked" ||
              selectedTask.status === "in_progress") &&
              !selectedTask.escalated && (
                <button
                  onClick={() => {
                    const reason = window.prompt("请输入升级原因：");
                    if (reason && reason.trim()) {
                      escalateTask(selectedTask.id, reason.trim());
                    }
                  }}
                  className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-red-300 bg-red-500/10 hover:bg-red-500/20 transition-colors"
                >
                  <ArrowUpCircle size={10} />
                  升级
                </button>
              )}
            {/* 跨模块工作流触发 */}
            <button
              onClick={() => handleTrigger(selectedTask)}
              disabled={isTriggering || !canTrigger}
              className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-purple-300 bg-purple-500/15 hover:bg-purple-500/25 transition-colors disabled:opacity-40"
              title={
                canTrigger
                  ? `向 ${toMeta.label}（${toRuntimeName}）触发工作流`
                  : `${toMeta.label} 未绑定 Runtime，无法触发`
              }
            >
              {isTriggering ? (
                <RefreshCw size={10} className="animate-spin" />
              ) : (
                <Zap size={10} />
              )}
              触发跨模块工作流
            </button>
            {/* 删除 */}
            <button
              onClick={() => {
                if (window.confirm("确认删除该协调任务？")) {
                  deleteTask(selectedTask.id);
                }
              }}
              className="ml-auto flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-gray-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
            >
              <Trash2 size={10} />
              删除
            </button>
          </div>
          {!canTrigger && (
            <p className="text-[8px] font-mono text-gray-600 mt-2">
              {toMeta.label} 未绑定 Runtime，跨模块触发不可用
            </p>
          )}
        </div>

        {/* 沟通记录 */}
        <CommunicationPanel
          task={selectedTask}
          onAdd={(from, content) => addCommunication(selectedTask.id, from, content)}
        />

        {/* 协作效率（按部门） */}
        <DeptEfficiencyCard dept={selectedTask.toDept} stats={deptStats[selectedTask.toDept]} />
      </div>
    );
  };

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带（紫色主色调） */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
            <Network size={13} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">
              Hermes 跨部门协调
            </h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Cross-Department Coordination
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 筛选切换 */}
          <button
            onClick={() => setShowFilters((v) => !v)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border transition-colors ${
              showFilters ||
              filterStatus !== "all" ||
              filterPriority !== "all" ||
              filterDept !== "all"
                ? "bg-purple-500/15 border-purple-500/30 text-purple-300"
                : "bg-white/[0.03] border-white/[0.06] text-gray-400 hover:bg-white/[0.05]"
            }`}
            title="筛选协调任务"
          >
            <Filter size={11} />
            <span className="text-[9px] font-mono uppercase tracking-wider">筛选</span>
          </button>
          {/* 创建任务 */}
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors"
            title="创建新的跨部门协调任务"
          >
            <Plus size={11} className="text-purple-400" />
            <span className="text-[9px] font-mono text-purple-300 uppercase tracking-wider">
              创建任务
            </span>
          </button>
        </div>
      </div>

      {/* Toast 提示 */}
      <AnimatePresence>
        {toast && (
          <motion.div
            key={toast}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-b border-white/[0.04]"
          >
            <div className="px-4 py-2">
              <div className="px-3 py-2 rounded-md border border-purple-500/30 bg-purple-500/[0.06] text-[10px] font-mono text-purple-300">
                {toast}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 筛选栏 */}
      {renderFilters()}

      {/* 顶部：统计卡片 + 部门协作热力图 + 跨模块状态 */}
      <div className="p-4 space-y-3 border-b border-white/[0.04]">
        {renderStatsBar()}
        <div className="grid grid-cols-2 gap-3">
          {renderHeatmap()}
          {renderDeptRuntimeStatus()}
        </div>
      </div>

      {/* 中部：协调任务看板 */}
      <div className="p-4 border-b border-white/[0.04]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            协调任务看板
          </span>
          <span className="text-[9px] font-mono text-gray-600">
            共 {filteredTasks.length} / {store.tasks.length} 项
          </span>
        </div>
        {renderKanban()}
      </div>

      {/* 底部：选中任务详情 */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            任务详情与操作
          </span>
          {selectedTask && (
            <button
              onClick={() => setSelectedTaskId(null)}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-mono text-gray-500 hover:text-white hover:bg-white/[0.05] transition-colors"
            >
              <X size={9} />
              关闭
            </button>
          )}
        </div>
        {renderTaskDetail()}
      </div>

      {/* 创建任务弹窗 */}
      <AnimatePresence>
        {showCreate && (
          <CreateTaskModal
            onClose={() => setShowCreate(false)}
            onSubmit={createTask}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 子组件：沟通记录面板
// ============================================
interface CommunicationPanelProps {
  task: CoordinationTask;
  onAdd: (from: string, content: string) => void;
}

function CommunicationPanel({ task, onAdd }: CommunicationPanelProps) {
  const [from, setFrom] = useState("");
  const [content, setContent] = useState("");

  const submit = () => {
    if (!content.trim()) return;
    onAdd(from, content);
    setFrom("");
    setContent("");
  };

  return (
    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <MessageSquare size={11} style={{ color: PRIMARY }} />
          <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
            沟通记录
          </span>
        </div>
        <span className="text-[9px] font-mono text-gray-600">
          {task.communications.length} 条
        </span>
      </div>

      {/* 沟通列表 */}
      {task.communications.length === 0 ? (
        <div className="py-4 text-center">
          <p className="text-[9px] font-mono text-gray-600">暂无沟通记录</p>
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1 mb-2">
          {task.communications.map((c) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              className="rounded border border-white/[0.04] bg-white/[0.02] px-2 py-1.5"
            >
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-[9px] font-mono text-purple-300">{c.from}</span>
                <span className="text-[8px] font-mono text-gray-600">
                  {formatRelativeTime(c.timestamp)}
                </span>
              </div>
              <p className="text-[10px] text-gray-300 leading-snug">{c.content}</p>
            </motion.div>
          ))}
        </div>
      )}

      {/* 输入区 */}
      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/[0.04]">
        <input
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          placeholder="发送人/部门"
          className="w-24 px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] font-mono text-white placeholder:text-gray-600 outline-none focus:border-purple-500/40"
        />
        <input
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          placeholder="输入沟通内容后回车发送..."
          className="flex-1 px-2 py-1 rounded bg-white/[0.04] border border-white/[0.08] text-[10px] font-mono text-white placeholder:text-gray-600 outline-none focus:border-purple-500/40"
        />
        <button
          onClick={submit}
          disabled={!content.trim()}
          className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono text-purple-300 bg-purple-500/15 hover:bg-purple-500/25 transition-colors disabled:opacity-40"
        >
          <Send size={10} />
          发送
        </button>
      </div>
    </div>
  );
}

// ============================================
// 子组件：部门协作效率卡片
// ============================================
interface DeptEfficiencyCardProps {
  dept: Department;
  stats: {
    total: number;
    completed: number;
    blocked: number;
    escalated: number;
    completionRate: number;
    avgDuration: number;
  };
}

function DeptEfficiencyCard({ dept, stats }: DeptEfficiencyCardProps) {
  const meta = DEPT_META[dept];
  const Icon = meta.icon;
  return (
    <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon size={11} style={{ color: meta.color }} />
          <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">
            接收部门协作效率
          </span>
        </div>
        <span className="text-[9px] font-mono" style={{ color: meta.color }}>
          {meta.label}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded border border-white/[0.04] bg-white/[0.01] px-2 py-1.5">
          <div className="text-[8px] font-mono text-gray-500 uppercase">参与任务</div>
          <div className="text-[12px] font-mono font-bold text-white">{stats.total}</div>
        </div>
        <div className="rounded border border-white/[0.04] bg-white/[0.01] px-2 py-1.5">
          <div className="text-[8px] font-mono text-gray-500 uppercase">完成率</div>
          <div className="text-[12px] font-mono font-bold text-emerald-400">
            {stats.completionRate.toFixed(0)}%
          </div>
        </div>
        <div className="rounded border border-white/[0.04] bg-white/[0.01] px-2 py-1.5">
          <div className="text-[8px] font-mono text-gray-500 uppercase">平均耗时</div>
          <div className="text-[12px] font-mono font-bold text-purple-300">
            {stats.avgDuration > 0 ? `${stats.avgDuration.toFixed(1)}h` : "—"}
          </div>
        </div>
      </div>
      {(stats.blocked > 0 || stats.escalated > 0) && (
        <div className="flex items-center gap-3 mt-2 text-[9px] font-mono">
          <span className="text-amber-400">阻塞 {stats.blocked}</span>
          <span className="text-red-400">升级 {stats.escalated}</span>
        </div>
      )}
    </div>
  );
}

// ============================================
// 子组件：创建任务弹窗
// ============================================
interface CreateTaskModalProps {
  onClose: () => void;
  onSubmit: (data: {
    title: string;
    description: string;
    priority: Priority;
    fromDept: Department;
    toDept: Department;
    assignee?: string;
    dueDate?: string;
  }) => void;
}

function CreateTaskModal({ onClose, onSubmit }: CreateTaskModalProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<Priority>("medium");
  const [fromDept, setFromDept] = useState<Department>("ceo");
  const [toDept, setToDept] = useState<Department>("marketing");
  const [assignee, setAssignee] = useState("");
  const [dueDate, setDueDate] = useState("");

  const submit = () => {
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      description: description.trim(),
      priority,
      fromDept,
      toDept,
      assignee: assignee.trim() || undefined,
      dueDate: dueDate || undefined,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-30 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 8, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 8, scale: 0.98 }}
        className="w-full max-w-md rounded-lg border border-purple-500/30 bg-[#13101c]/95 backdrop-blur-xl shadow-2xl shadow-purple-500/20 overflow-hidden mt-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-400/60 to-transparent" />
        {/* 弹窗头部 */}
        <div className="flex items-center justify-between p-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <Plus size={12} className="text-purple-400" />
            <span className="text-[11px] font-mono text-white uppercase tracking-wider">
              创建跨部门协调任务
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded text-gray-500 hover:text-white hover:bg-white/[0.06] transition-colors"
          >
            <X size={12} />
          </button>
        </div>

        {/* 表单 */}
        <div className="p-3 space-y-2.5">
          {/* 标题 */}
          <div>
            <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              任务标题 *
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="例如：协调营销中心产出 Q3 品牌活动方案"
              autoFocus
              className="mt-1 w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[11px] text-white placeholder:text-gray-600 outline-none focus:border-purple-500/40"
            />
          </div>
          {/* 描述 */}
          <div>
            <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              任务描述
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="说明任务背景、目标与具体要求..."
              rows={3}
              className="mt-1 w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[11px] text-white placeholder:text-gray-600 outline-none focus:border-purple-500/40 resize-none"
            />
          </div>
          {/* 部门流向 */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                发起部门
              </label>
              <select
                value={fromDept}
                onChange={(e) => setFromDept(e.target.value as Department)}
                className="mt-1 w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[11px] text-white outline-none focus:border-purple-500/40"
              >
                {(Object.keys(DEPT_META) as Department[]).map((d) => (
                  <option key={d} value={d}>
                    {DEPT_META[d].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                接收部门
              </label>
              <select
                value={toDept}
                onChange={(e) => setToDept(e.target.value as Department)}
                className="mt-1 w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[11px] text-white outline-none focus:border-purple-500/40"
              >
                {(Object.keys(DEPT_META) as Department[]).map((d) => (
                  <option key={d} value={d}>
                    {DEPT_META[d].label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* 优先级 + 截止 */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                优先级
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as Priority)}
                className="mt-1 w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[11px] text-white outline-none focus:border-purple-500/40"
              >
                {(Object.keys(PRIORITY_META) as Priority[]).map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_META[p].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                截止日期
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="mt-1 w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[11px] text-white outline-none focus:border-purple-500/40"
              />
            </div>
          </div>
          {/* 负责人 */}
          <div>
            <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              负责人（可选）
            </label>
            <input
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              placeholder="接收部门对接人姓名"
              className="mt-1 w-full px-2 py-1.5 rounded bg-white/[0.04] border border-white/[0.08] text-[11px] text-white placeholder:text-gray-600 outline-none focus:border-purple-500/40"
            />
          </div>
        </div>

        {/* 底部操作 */}
        <div className="flex items-center justify-end gap-2 p-3 border-t border-white/[0.06]">
          <button
            onClick={onClose}
            className="px-3 py-1 rounded text-[10px] font-mono text-gray-400 bg-white/[0.03] hover:bg-white/[0.05] transition-colors"
          >
            取消
          </button>
          <button
            onClick={submit}
            disabled={!title.trim()}
            className="flex items-center gap-1 px-3 py-1 rounded text-[10px] font-mono text-purple-300 bg-purple-500/15 hover:bg-purple-500/25 transition-colors disabled:opacity-40"
          >
            <Pencil size={10} />
            创建
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
